import { Switch } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { UpsertContactButton } from 'contacts/components';
import { Location } from 'contacts/elements';
import { ContactGroupType } from 'contacts/enums';
import { IContact, IContactGroup } from 'contacts/types';
import { InfoCard, SlidingPanelContent } from 'core/components';
import { Email } from 'core/elements';
import { PhoneNumber } from 'core/elements';
import { AlternateVersionHelper } from 'core/helpers';
import { Variant } from 'theme/enums';

import { InvitationButton } from './InvitationButton';

interface IProps {
  contact: IContact;
  contactGroupType: ContactGroupType;
  onEditContactSuccess?: (contact: IContact) => void;
}

export const Information = ({ contact, contactGroupType, onEditContactSuccess }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const handleOnEditContactSuccess = (updatedContact: IContact, contactGroup: IContactGroup) => {
    onEditContactSuccess?.({ ...updatedContact, contactGroup });
  };

  const handleInvitationSuccess = (updatedContact: IContact, contactGroup: IContactGroup) => {
    onEditContactSuccess?.({ ...updatedContact, contactGroup });
  };

  const isDeleted = Boolean(contact?.deleted);

  const slidingPanelExtra = (
    <UpsertContactButton
      contact={contact}
      onSubmitSuccess={handleOnEditContactSuccess}
      level={1}
      disabled={isDeleted}
    />
  );

  const isVendorContact = contactGroupType === ContactGroupType.VENDOR;

  const items = [
    { label: t('labelFirstName'), value: contact.firstName },
    { label: t('labelLastName'), value: contact.lastName },
    { label: t('labelEmail'), value: <Email email={contact.email} /> },
    { label: t('labelOfficePhoneNumber'), value: <PhoneNumber phone={contact.phone} /> },
    { label: t('labelMobilePhoneNumber'), value: contact.mobile },
    ...(!isAlternateVersionEnabled
      ? [{ label: t('labelContactGrouping'), value: contact.contactGroupingName }]
      : []),
    {
      label: t('labelDefaultBillTo'),
      value: (
        <Location
          contactGroupAddress={contact.billingAddress}
          contactGroupType={contactGroupType}
          displayName
        />
      ),
    },
    {
      label: t('labelDefaultShipTo'),
      value: (
        <Location
          contactGroupAddress={contact.shippingAddress}
          contactGroupType={contactGroupType}
          displayName
        />
      ),
    },
    ...(!isAlternateVersionEnabled
      ? [
          {
            label: t('labelDefaultSoldTo'),
            value: (
              <Location
                contactGroupAddress={contact.sellingAddress}
                contactGroupType={contactGroupType}
                displayName
              />
            ),
          },
        ]
      : []),
    ...(isVendorContact && contact.isDefault !== undefined
      ? [
          {
            label: t('labelDefault'),
            value: (
              <Switch checked={Boolean(contact.isDefault)} disabled variant={Variant.OUTLINED} />
            ),
          },
        ]
      : []),
  ];

  return (
    <SlidingPanelContent title={t('titleInformation')} extra={slidingPanelExtra} fullWidth={false}>
      <InfoCard items={items} />

      <InvitationButton
        contact={contact}
        onSuccess={handleInvitationSuccess}
        disabled={isDeleted}
      />
    </SlidingPanelContent>
  );
};
